<template>
  <section class="box checkOut-page">
    <div v-if="isGetUrlLoading" class="payment-overlay">
      <div class="overlay-content">
        <v-progress-circular
          indeterminate
          color="#BD9478"
        ></v-progress-circular>
        <span>מעביר לדף תשלום</span>
      </div>
    </div>
    <div class="checkout-columns">
      <div class="right-side">
        <div class="right-side-wrapper sticky">
        <!-- Back Button -->
        <div @click="$router.go(-1)" class="icon-wrapper">
          <div class="icon-bg" @click="goBack">
            <v-icon size="15" color="#BD9478" left>mdi-arrow-right</v-icon>
          </div>
          <span>{{ $t("checkOut.back") }}</span>
        </div>

        <!-- Alert -->
        <minOrderAlert width="fit-content" />

        <!-- Delivery Date Selection -->
        <div class="delivery-date">
          <div class="d-flex ga-2 align-center">
            <v-icon icon="mdi-truck-delivery" color="#BD9478"></v-icon>
            <h4>{{ $t("checkOut.titleDelivery") }}</h4>
          </div>
          <div>
            <div
              v-if="getUserCustomerAddress"
              class="d-flex ga-2 mr-7 align-center"
            >
              <v-icon size="15" icon="mdi-map-marker"></v-icon>
              <span>{{ $t("checkOut.deliveryAddress") }}</span>
              <span class="deepGray">{{ getUserCustomerAddress }}</span>
            </div>
          </div>
          <div class="radio-boxes">
            <div
              class="radio-box"
              :class="{ active: selectedDeliveryTime === 'nearest' }"
              @click="selectedDeliveryTime = 'nearest'"
            >
              <div class="radio-content mr-8">
                <div class="radio-header">
                  <v-icon size="35">mdi-clipboard-check-outline</v-icon>
                  <span class="title">{{
                    $t("checkOut.nearestDelivery")
                  }}</span>
                </div>
                <p class="context">
                  {{
                    $t("checkOut.nereastDate").replace(
                      "%s",
                      getNumberOfDaysToDelivery
                    )
                  }}
                </p>
              </div>
              <div class="radio-indicator"></div>
            </div>

            <div
              class="radio-box"
              :class="{ active: selectedDeliveryTime === 'later' }"
              @click="openDatePicker"
            >
              <div class="radio-content mt-2 mr-8">
                <div class="radio-header">
                  <span
                    v-if="formattedSelectedDate"
                    class="label date-picked-label"
                    >{{ formattedSelectedDate }}</span
                  >
                  <v-icon size="35">mdi-calendar</v-icon>
                  <span class="title">{{ $t("checkOut.laterDelivery") }}</span>
                </div>
                <p class="context">{{ $t("checkOut.chooseDateText") }}</p>
              </div>
              <div class="radio-indicator"></div>
            </div>
          </div>
        </div>

        <!-- Additional Notes -->
        <div class="notes-section">
          <div class="d-flex align-center ga-2 mb-3">
            <v-icon icon="mdi-note-edit" color="#BD9478"></v-icon>
            <h4>{{ $t("checkOut.noteTitle") }}</h4>
          </div>
          <v-textarea
            variant="outlined"
            rows="4"
            v-model="orderNotes"
            :placeholder="$t('checkOut.notePlaceholder')"
            maxlength="400"
            counter
          ></v-textarea>
        </div>

        <!-- Payment Method -->
        <div class="payments">
          <div class="d-flex ga-2 align-center">
            <v-icon icon="mdi-circle-multiple" color="#BD9478"></v-icon>
            <h4>{{ $t("checkOut.titlePayment") }}</h4>
          </div>
          <div class="">
            <div class="radio-boxes">
              <div
                class="radio-box"
                :class="{
                  active: selectedPayment === 'obligo',
                  disable: isUserNotAllowToPayWithObligo,
                }"
                @click="selectedPayment = 'obligo'"
              >
                <div class="radio-content mr-8">
                  <div class="radio-header">
                    <v-icon size="35">mdi-bank</v-icon>
                    <span class="title">{{
                      $t("checkOut.obligoPayment")
                    }}</span>
                  </div>
                  <p v-if="userBalance < 0" class="context">
                    {{ $t("checkOut.creditBalance") }}
                    <span class="amount">₪{{ userBalance * -1 }}</span>
                  </p>
                  <p v-else class="context">
                    {{ $t("checkOut.obligoUsage") }}
                    <span class="amount"
                      >₪{{ (userBalance || 0).toLocaleString() }}</span
                    >
                  </p>
                  <p class="context">
                    {{ $t("checkOut.obligoLimit") }}
                    <span class="amount">₪{{ (obligo || 0).toLocaleString() }}</span>
                  </p>
                </div>
                <div class="radio-indicator"></div>
              </div>

              <div
                class="radio-box"
                :class="{
                  active: selectedPayment === 'card',
                  disable: isCreditPaymentDisabled,
                }"
                @click="selectedPayment = 'card'"
              >
                <div class="radio-content mt-2 mr-8">
                  <div class="radio-header">
                    <span v-if="false" class="label payment-label">{{
                      $t("checkOut.discountLabelCredit")
                    }}</span>
                    <v-icon size="35">mdi-credit-card-outline</v-icon>
                    <span class="title">{{ $t("checkOut.cardPayment") }}</span>
                  </div>
                  <p class="context">
                    {{ $t("checkOut.creditCardMethodText") }}
                  </p>
                </div>
                <div class="radio-indicator"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- Alerts Section -->
        <div class="alerts-section">
          <Alert
            v-if="isUserExceededMaxBalance"
            mode="warning"
            class="mb-2"
            :alertContent="$t('checkOut.exceededMaxBalance')"
          ></Alert>
          <Alert
            v-else-if="isUserNotAllowToPayWithObligo"
            mode="warning"
            class="mb-2"
            :alertContent="$t('checkOut.notAllowToPayWithObligo')"
          ></Alert>
          <Alert
            v-else-if="!isUserReachTheMinOrder && websitePolicyAproved"
            mode="warning"
            class="mb-2"
            :alertContent="`${$t('checkOut.remainingAmount')} ₪${formatPrice(
              minOrderSum -
                (getTotalCartBeforeVAT + getTotalCartBeforeVAT * getVat)
            )} ${$t('checkOut.toOrder')}`"
          ></Alert>
        </div>
        <!-- webSite Policy  -->
        <div>
          <div class="d-flex flex-colum align-center website-policy">
            <div class="d-flex align-center ga-2 mb-5">
              <input
                class="checkbox"
                type="checkbox"
                :disabled="getCartOrderLinesNumber === 0"
                v-model="websitePolicyAproved"
              />
              <span>
                {{ $t("checkOut.websitePolicyAgree") }}
                <span @click="$router.push('policy')" class="link">{{
                  $t("checkOut.websitePolicyLink")
                }}</span>
              </span>
            </div>
          </div>
          <div>
            <v-btn
              :disabled="isUserCannotSendOrder"
              @click="sendOrder"
              :loading="isLoading"
              height="60"
              width="300"
              color="#bd9478"
              ><span>{{ $t("SingleOrder.SendOrder") }}</span></v-btn
            >
          </div>
        </div>
        <!-- send order -->
        </div>
      </div>

      <div class="left-side">
        <Cart :isCartOpen="true" :isCheckOut="true" />
      </div>
    </div>

    <!-- Maybe you missed — related products from the cart's categories -->
    <div v-if="missedProductsList.length" class="missed-products">
      <h2 class="missed-title">אולי פספסתם…</h2>
      <ProductsCarousel
        :products="missedProductsList"
        :breakpoints="breakpoints"
        :isAuthenticated="isAuthenticated"
        :priceListId="getCustomerPriceListId"
        :slideWidthPercent="88"
        @updateProduct="updateProduct"
        @updatePartRquestAddToPriceList="updatePartRquestAddToPriceList"
      />
    </div>

    <!-- Date Picker Dialog -->
    <v-dialog v-model="showDatePicker" width="360" class="pa-0">
      <v-card class="pa-3 card">
        <v-btn icon @click="showDatePicker = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-card-text>
          <v-date-picker
            class="pa-0"
            title="בחירת תאריך"
            header=""
            v-model="selectedDate"
            :min="minDate"
            :allowed-dates="allowedDates"
            full-width
            @input="handleDateSelection"
          ></v-date-picker>
        </v-card-text>
      </v-card>
    </v-dialog>
    <ThankYouPopUp
      :isUserRatingHasBeenSent="isUserRatingHasBeenSent"
      :isSendRatingUserLoading="isSendRatingUserLoading"
      :isResetRatingUserForm="isResetRatingUserForm"
      :userRatingFormError="userRatingFormError"
      @rate="sendUserRating"
      :ord="newOrd"
      :value="isThankYouPopupDisplay"
    />
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import Cart from "../menus/Cart.vue";
import minOrderAlert from "../helpers/Cart/minOrderAlert.vue";
import moment from "moment";
import Alert from "../helpers/Alert.vue";
import ThankYouPopUp from "../UI/PopUps/ThankYouPopUp.vue";
import { formatPrice } from "@/utilities";
import RatingService from "../../services/RatingService";
import paymentService from "../../services/PaymentService";
import ProductsCarousel from "../helpers/homePage/ProductsCarousel.vue";

export default {
  components: {
    Cart,
    minOrderAlert,
    Alert,
    ThankYouPopUp,
    ProductsCarousel,
  },

  async mounted() {
    // Reaching checkout from an already-scrolled listing page should land at
    // the top, same as every other page — force it explicitly rather than
    // relying only on the router's scrollBehavior.
    window.scrollTo({ top: 0, behavior: "auto" });
    this.$store.dispatch("Orders/fetchShippingTiers");
    // make the selectedDate to be the nearestDate by default
    this.selectedDate = moment(this.nearestDate).toDate();
    this.getCustomerBalance();
    if (this.isUserNotAllowToPayWithObligo) {
      this.selectedPayment = "card";
    }
    // "אולי פספסתם" products are fetched by the getCartOrderLinesNumber
    // watcher below (immediate), once the cart is actually populated — it
    // can still be loading here on a hard refresh straight into /checkOut.
  },

  data() {
    return {
      isLoading: false,
      isGetUrlLoading: false,
      missedProductsList: [],
      missedProductsFetched: false,
      isQuantityBtnDisable: false,
      breakpoints: {
        200: { itemsToShow: 1, snapAlign: "start" },
        768: { itemsToShow: 1, snapAlign: "start" },
        1024: { itemsToShow: 2, snapAlign: "start" },
        1600: { itemsToShow: 3, snapAlign: "start" },
      },
      isSendRatingUserLoading: false,
      isUserRatingHasBeenSent: false,
      userRatingFormError: "",
      isThankYouPopupDisplay: false,
      newOrd: 0,
      orderNotes: "",
      paymentMethod: "creditCard",
      selectedDeliveryTime: "nearest",
      selectedPayment: "obligo",
      websitePolicyAproved: false,
      menu: false,
      laterDeliveryDate: new Date().toISOString().substr(0, 10),
      showDatePicker: false,
      selectedDate: null,
      isResetRatingUserForm: false,
    };
  },

  watch: {
    selectedDate() {
      this.showDatePicker = false;
    },
    showDatePicker(value) {
      console.log(this.selectedDate);
      if (!value && !this.selectedDate) {
        this.selectedDeliveryTime = "nearest";
      }
    },
    selectedDeliveryTime(newValue) {
      if (newValue === "nearest") {
        this.selectedDate = moment(this.nearestDate).toDate();
      }
    },
    getCartOrderLinesNumber: {
      immediate: true,
      handler(count) {
        if (count > 0 && !this.missedProductsFetched) {
          this.missedProductsFetched = true;
          this.fetchMissedProducts();
        }
      },
    },
  },

  computed: {
    isCreditPaymentDisabled() {
      return this.isUserExceededMaxBalance;
    },
    isUserCannotSendOrder() {
      return (
        this.getCartOrderLinesNumber === 0 ||
        !this.websitePolicyAproved ||
        (this.selectedDeliveryTime === "later" && !this.selectedDate) ||
        !this.nearestDate ||
        !this.isUserReachTheMinOrder ||
        this.isCreditPaymentDisabled
      );
    },
    isUserReachTheMinOrder() {
      return (
        this.minOrderSum -
          (this.getTotalCartBeforeVAT +
            this.getTotalCartBeforeVAT * this.getVat) <=
        0
      );
    },
    isUserNotAllowToPayWithObligo() {
      return this.obligo === 0 || this.isUserExceededMaxBalance;
    },
    userBalance() {
      return this.getUser?.Customer?.balance;
    },
    obligo() {
      return this.getUser?.Customer?.maxBalance;
    },
    isUserExceededMaxBalance() {
      return (
        (this.userBalance >= this.obligo ||
          this.getCartTotalPrice > this.obligo) &&
        this.obligo > 0
      );
    },
    formatNereastDate() {
      moment.locale("he");
      return this.nearestDate.format("dddd, D MMMM");
    },
    nearestDate() {
      let deliveryDate = moment().add(
        this.getNumberOfDaysToDelivery + 1,
        "days"
      );

      // Loop until the date is not Friday (5) or Saturday (6)
      while (deliveryDate.day() === 5 || deliveryDate.day() === 6) {
        deliveryDate.add(1, "days");
      }

      return deliveryDate;
    },
    ...mapGetters({
      getUser: "AuthModule/getUser",
      minOrderSum: "Orders/getMinOrderSum",
      getNumberOfDaysToDelivery: "Orders/getNumberOfDaysToDelivery",
      getCartOrderLinesNumber: "Orders/getCartOrderLinesNumber",
      getTotalCartBeforeVAT: "Orders/getTotalCartBeforeVAT",
      getCartTotalPrice: "Orders/getCartTotalPrice",
      getUserCustomerAddress: "AuthModule/userCustomerAddress",
      getVat: "Orders/getTaxPercent",
      getLineByPart: "Orders/getLineByPart",
      getCustomerPriceListId: "AuthModule/getCustomerPriceListId",
      isAuthenticated: "AuthModule/isAuthenticated",
    }),
    minDate() {
      const date = new Date();
      date.setDate(date.getDate() + this.getNumberOfDaysToDelivery);
      return date.toISOString().substr(0, 10);
    },

    formattedSelectedDate() {
      if (!this.selectedDate) return "";
      const date = new Date(this.selectedDate);
      return new Intl.DateTimeFormat("he-IL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    },
  },

  methods: {
    async fetchMissedProducts() {
      try {
        const orderLines =
          this.$store.getters["Orders/getCart"]?.OrderLines || [];
        const categoryIds = [
          ...new Set(
            orderLines
              .map((line) => line.Part?.category?.categoryId)
              .filter((id) => id && id !== -1),
          ),
        ];
        const excludePartIds = orderLines
          .map((line) => line.Part?.part)
          .filter(Boolean);
        const res = await this.$store.dispatch(
          "Parts/fetchRelatedToCartProducts",
          { categoryIds, excludePartIds },
        );
        this.missedProductsList = res?.parts || [];
      } catch (error) {
        console.log(error);
      }
    },
    async updateProduct(obj) {
      if (!obj?.product || this.isQuantityBtnDisable) return;

      this.isQuantityBtnDisable = true;
      const { product, quantity } = obj;
      const lineOrdi = this.getLineByPart(product.part);

      const line = {
        Part: { ...product },
        quant: quantity,
        ...(lineOrdi && { ordi: lineOrdi }),
      };

      if (quantity > 0) {
        await this.$store.dispatch("Orders/updateCart", line);
      } else if (line.ordi) {
        await this.$store.dispatch("Orders/removeProductFromCart", line);
      }

      this.isQuantityBtnDisable = false;
    },
    updatePartRquestAddToPriceList(partId) {
      const idx = this.missedProductsList.findIndex((p) => p.part === partId);
      if (idx !== -1) {
        this.missedProductsList[idx] = {
          ...this.missedProductsList[idx],
          sentPriceListRequest: true,
        };
      }
    },
    async sendUserRating({ rating, reason }) {
      if (rating >= 4) return;
      try {
        this.userRatingFormError = "";
        this.isSendRatingUserLoading = true;
        const response = await RatingService.sendUserRating({
          rating,
          reason,
        });
        if (response.data.success) {
          this.isUserRatingHasBeenSent = true;
        }
      } catch (error) {
        console.error("Error sending user rating:", error);
        this.userRatingFormError =
          error?.response?.data?.error ??
          "שגיאה בניסיון שליחת הדירוג, אנא נסו שנית מאוחר יותר";
      } finally {
        this.isSendRatingUserLoading = false;
      }
    },
    formatPrice(price) {
      return formatPrice(price);
    },
    async getCustomerBalance() {
      try {
        await this.$store.dispatch("AuthModule/getCustomerBalance");
      } catch (error) {
        console.error("Error updating customer balance:", error);
      }
    },
    formatOrderLines(orderLines) {
      return orderLines.map((line) => ({
        ...line,
        partPrice:
          line.Part.discountPrice > 0
            ? line.Part.discountPrice
            : line.Part.originPrice,
        totalPriceSum:
          (line.Part.discountPrice > 0
            ? line.Part.discountPrice
            : line.Part.originPrice) * line.quant,
      }));
    },
    async sendOrder() {
      try {
        const payload = {
          notes: this.orderNotes,
          paymentMethod: this.selectedPayment,
          duedate:
            this.selectedDeliveryTime === "nearest"
              ? this.nearestDate
              : this.selectedDate,
          OrderLines: this.formatOrderLines(
            this.$store.getters["Orders/getCart"].OrderLines
          ),
        };

        this.isLoading = true;
        if (this.selectedPayment === "card") {
          // get url payment and redirect it to the payment page
          this.isGetUrlLoading = true;
          const paymentUrl = await paymentService.getUrlPaymentPage(payload);
          const url = paymentUrl?.data?.url;
          // set the payload in localstorage
          localStorage.setItem("cartPayload", JSON.stringify(payload));
          if (url) {
            window.location.href = url;
          } else {
            this.isGetUrlLoading = false;
            throw new Error();
          }
          return;
        }
        const response = await this.$store.dispatch(
          "Orders/sendOrder",
          payload
        );

        if (response?.ord) {
          this.newOrd = response.ord;
          this.isThankYouPopupDisplay = true;
        }
      } catch (error) {
        console.log(error);
        this.$store.dispatch(
          "ShowToast",
          {
            status: "error",
            message:
              error.response.data.error ??
              "ארעה שגיאה, מומלץ לרענן את הדף ולנסות שוב",
          },
          { root: true }
        );
      } finally {
        this.isLoading = false;
        this.isGetUrlLoading = false;
        this.websitePolicyAproved = false;
      }
    },
    openDatePicker() {
      this.selectedDeliveryTime = "later";
      this.showDatePicker = true;
    },

    handleDateSelection(date) {
      this.selectedDate = date;
      this.showDatePicker = false;
    },

    allowedDates(date) {
      const currentDate = new Date();
      const selectedDate = new Date(date);
      const minAllowedDate = new Date();
      minAllowedDate.setDate(
        currentDate.getDate() + this.getNumberOfDaysToDelivery
      );

      // Ensure the selected date is after the minAllowedDate
      if (selectedDate < minAllowedDate) {
        return false;
      }

      // Skip Friday (5) and Saturday (6)
      const day = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 5 = Friday, 6 = Saturday
      if (day === 5 || day === 6) {
        return false;
      }

      return true;
    },
  },
};
</script>
<style lang="scss">
.payment-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;

  .overlay-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;

    span {
      font-size: 20px;
      color: #bd9478;
    }
  }
}
.card {
  .v-card-text {
    padding: 0px !important;
  }
  .v-sheet {
    width: fit-content;
  }
}
</style>

<style lang="scss" scoped>
.checkOut-page {
  display: flex;
  flex-direction: column;
  gap: 50px;
  .checkout-columns {
    display: flex;
    gap: 50px;
  }
  .missed-products {
    .missed-title {
      text-align: center;
      font-family: Heebo, sans-serif;
      font-size: 28px;
      font-weight: 400;
      margin-bottom: 20px;
    }
  }
  .right-side {
    flex-basis: 60%;
    .sticky {
      position: sticky;
      top: 30px;
      &.right-side-wrapper {
        display: flex;
        flex-direction: column;
        gap: 30px;
      }
    }
  }

  .left-side {
    flex-basis: 40%;
    // background: #f9f9f9;
    border: 1px solid #e0e0e0;
    padding: 20px;
    border-radius: 10px;
    height: fit-content;

    .order-summary {
      margin-bottom: 20px;

      .summary-item,
      .summary-total {
        display: flex;
        justify-content: space-between;
        margin: 10px 0;
      }

      .summary-total {
        font-weight: bold;
        font-size: 18px;
      }
    }

    .submit-btn {
      margin-top: 20px;
    }
  }

  .delivery-date,
  .payments {
    .radio-boxes {
      display: flex;
      gap: 15px;
      margin-top: 10px;
    }

    .radio-box {
      flex: 1;
      border: 1px solid $tableOrderColor;
      border-radius: 8px;
      padding: 15px;
      cursor: pointer;
      position: relative;
      transition: all 0.3s ease;

      &:hover {
        border-color: #bd9478;
      }

      &.active {
        border-color: #bd9478;

        .radio-indicator {
          &::after {
            transform: scale(1);
          }
        }
      }

      &.disable {
        opacity: 0.5;
        pointer-events: none;
      }

      .radio-content {
        .radio-header {
          display: flex;
          align-items: center;
          position: relative;
          gap: 8px;
          margin-bottom: 8px;
          .label {
            position: absolute;
            top: -40px;
            padding: 3px 10px;
            border-radius: 50px;
            &.date-picked-label {
              background: $primaryColor;
              color: white;
            }
            &.payment-label {
              background: $tableOrderColor;
            }
          }

          .title {
            font-weight: 600;
            color: #333;
            font-size: 22px;
          }

          .v-icon {
            color: black;
          }
        }

        .context {
          color: $DeepGray;
          font-size: 18px;
          margin: 0;

          .amount {
            color: black;
            font-weight: 700;
            font-size: 18px;
          }
        }
      }

      .radio-indicator {
        position: absolute;
        top: 25px;
        right: 15px;
        width: 20px;
        height: 20px;
        border: 2px solid $primaryColor;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;

        &::after {
          content: "";
          position: absolute;
          width: 10px;
          height: 10px;
          background-color: $primaryColor;
          border-radius: 50%;
          transform: scale(0);
          transition: transform 0.2s ease;
        }
      }

      &.active .radio-indicator::after {
        transform: scale(1);
      }
    }
  }
  h4 {
    font-size: 22px;
    font-weight: 400;
  }

  .alert {
    border-radius: 10px;
    .v-alert__content {
      color: white !important;
    }
    .text {
      font-weight: 300;
      b {
        font-weight: 700;
      }
    }
  }

  .icon-wrapper {
    margin-bottom: 20px;
    display: flex;
    gap: 10px;
    color: $primaryColor;

    &:hover {
      cursor: pointer;
    }

    .icon-bg {
      background: $litePrimaryColor;
      border-radius: 50%;
      padding: 1px 5px;
    }
  }
  .website-policy {
    font-size: 18px;
    span {
      padding: unset;
    }
    .link {
      text-decoration: underline;
      color: $primaryColor;
      cursor: pointer;
    }
  }
}
.isTablet,
.isMobile {
  .checkOut-page {
    // gap: 10px;
    .checkout-columns {
      flex-direction: column-reverse;
    }
    .right-side-wrapper {
      gap: 20px !important;
    }
    .left-side {
      flex-basis: 100%;
      padding: 0px;
    }
    .right-side {
      flex-basis: 100%;
      padding: 0px;
    }
    .radio-boxes {
      flex-direction: column !important;
      gap: 30px;
    }
  }
}
</style>
